import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const contentVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 8 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 380, damping: 30, mass: 0.8 },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    y: 4,
    transition: { duration: 0.15, ease: 'easeIn' },
  },
};

const ShareModal = ({ event, isOpen, onClose }) => {
  const [shareMethod, setShareMethod] = useState('link');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);

  const shareUrl = event ? `${window.location.origin}/events/${event.id || event.ID}` : '';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback copy also failed — nothing more to do
      }
      document.body.removeChild(textArea);
    }
  };

  const shareViaEmail = (platform) => {
    const subject = encodeURIComponent(`Check out this CliQQ: ${event.title}`);
    const body = encodeURIComponent(
      `${message || 'I thought you might be interested in this CliQQ!'}\n\n` +
        `${event.title}\n` +
        `${event.description}\n\n` +
        `Join here: ${shareUrl}`
    );

    let mailtoUrl;
    switch (platform) {
      case 'gmail':
        mailtoUrl = `https://mail.google.com/mail/?view=cm&to=${recipientEmail}&su=${subject}&body=${body}`;
        break;
      case 'outlook':
        mailtoUrl = `https://outlook.office.com/mail/compose?to=${recipientEmail}&subject=${subject}&body=${body}`;
        break;
      default:
        mailtoUrl = `mailto:${recipientEmail}?subject=${subject}&body=${body}`;
    }

    window.open(mailtoUrl, '_blank', 'noopener,noreferrer');
  };

  const shareToSocialMedia = (platform) => {
    const text = encodeURIComponent(`Check out this CliQQ: ${event.title}`);
    const url = encodeURIComponent(shareUrl);

    let socialUrl;
    switch (platform) {
      case 'twitter':
        socialUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'facebook':
        socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'whatsapp':
        socialUrl = `https://wa.me/?text=${text}%20${url}`;
        break;
      default:
        return;
    }

    window.open(socialUrl, '_blank', 'noopener,noreferrer');
  };

  const tabClass = (method) =>
    clsx(
      'px-4 py-2 rounded-xl text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
      shareMethod === method
        ? 'bg-primary-500 text-white shadow-soft-sm'
        : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
    );

  return (
    <Dialog.Root
      open={isOpen && !!event}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AnimatePresence>
        {isOpen && event && (
          <Dialog.Portal forceMount>
            {/* Overlay */}
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              />
            </Dialog.Overlay>

            {/* Content */}
            <Dialog.Content asChild>
              <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="w-full max-w-md bg-white rounded-2xl shadow-soft-lg overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 pt-6 pb-4">
                    <Dialog.Title className="text-xl font-bold text-surface-900">
                      Share CliQQ
                    </Dialog.Title>
                    <Dialog.Close
                      className="p-1.5 rounded-lg text-surface-400 hover:text-surface-700 hover:bg-surface-100 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                      aria-label="Close share dialog"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Dialog.Close>
                  </div>

                  <div className="px-6 pb-6 space-y-4">
                    {/* Event info */}
                    <div className="bg-surface-50 border border-surface-100 rounded-xl p-4">
                      <h3 className="font-semibold text-surface-900">{event.title}</h3>
                      <p className="text-sm text-surface-500 mt-1 line-clamp-2">
                        {event.description}
                      </p>
                      <p className="text-xs text-surface-400 mt-2">
                        {new Date(event.start_time || event.StartTime || event.datetime).toLocaleString()}
                      </p>
                    </div>

                    {/* Tab switcher */}
                    <div className="flex gap-2" role="tablist" aria-label="Share method">
                      {['link', 'email', 'social'].map((method) => (
                        <button
                          key={method}
                          role="tab"
                          aria-selected={shareMethod === method}
                          onClick={() => setShareMethod(method)}
                          className={tabClass(method)}
                        >
                          {method === 'link' ? 'Copy Link' : method.charAt(0).toUpperCase() + method.slice(1)}
                        </button>
                      ))}
                    </div>

                    {/* Copy Link panel */}
                    {shareMethod === 'link' && (
                      <div role="tabpanel">
                        <div className="flex rounded-xl overflow-hidden border border-surface-200">
                          <input
                            type="text"
                            value={shareUrl}
                            readOnly
                            aria-label="Event share URL"
                            className="flex-1 px-3 py-2.5 bg-surface-50 text-surface-700 text-sm focus:outline-none"
                          />
                          <button
                            onClick={copyToClipboard}
                            className={clsx(
                              'px-4 py-2.5 text-sm font-semibold border-l border-surface-200 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400',
                              copied
                                ? 'bg-green-500 text-white'
                                : 'bg-surface-50 text-surface-700 hover:bg-surface-100'
                            )}
                          >
                            {copied ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Email panel */}
                    {shareMethod === 'email' && (
                      <div role="tabpanel" className="space-y-3">
                        <div>
                          <label
                            htmlFor="share-recipient-email"
                            className="block text-sm font-medium text-surface-700 mb-1"
                          >
                            Recipient Email
                          </label>
                          <input
                            id="share-recipient-email"
                            type="email"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            placeholder="friend@example.com"
                            className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="share-message"
                            className="block text-sm font-medium text-surface-700 mb-1"
                          >
                            Personal Message
                            <span className="text-surface-400 font-normal ml-1">(optional)</span>
                          </label>
                          <textarea
                            id="share-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Add a personal message..."
                            rows={3}
                            className="w-full px-3 py-2.5 border border-surface-200 rounded-xl text-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            onClick={() => shareViaEmail('gmail')}
                            className="px-3 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                          >
                            Gmail
                          </button>
                          <button
                            onClick={() => shareViaEmail('outlook')}
                            className="px-3 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
                          >
                            Outlook
                          </button>
                          <button
                            onClick={() => shareViaEmail('default')}
                            className="px-3 py-2 bg-surface-500 text-white rounded-xl hover:bg-surface-600 text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-400"
                          >
                            Default
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Social media panel */}
                    {shareMethod === 'social' && (
                      <div role="tabpanel">
                        <div className="grid grid-cols-2 gap-3">
                          {/* Facebook */}
                          <button
                            onClick={() => shareToSocialMedia('facebook')}
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#1877F2] text-white rounded-xl hover:brightness-110 transition-all duration-150 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1877F2]"
                          >
                            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                            Facebook
                          </button>

                          {/* Twitter / X */}
                          <button
                            onClick={() => shareToSocialMedia('twitter')}
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-black text-white rounded-xl hover:bg-surface-800 transition-all duration-150 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-700"
                          >
                            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            Twitter
                          </button>

                          {/* LinkedIn */}
                          <button
                            onClick={() => shareToSocialMedia('linkedin')}
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#0A66C2] text-white rounded-xl hover:brightness-110 transition-all duration-150 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A66C2]"
                          >
                            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                            LinkedIn
                          </button>

                          {/* WhatsApp */}
                          <button
                            onClick={() => shareToSocialMedia('whatsapp')}
                            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white rounded-xl hover:brightness-110 transition-all duration-150 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
                          >
                            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                            </svg>
                            WhatsApp
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};

export default ShareModal;
